import { ChevronRight, GripVertical } from 'lucide-react';
import { type ReactNode } from 'react';
import { Button as AriaButton, Cell, Row } from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { formatPhoneNumber } from '~/util/functions';
import { Button } from '../components/buttons/Button';
import { Checkbox } from '~/shared/forms/controls/base-controls/Checkbox';
import type { DataTableColumnProps } from './DataTableColumn';
import { SuspenseWrapper } from '../SuspenseWrapper';
import { cn } from '../components/utils';

const dataTableRowStyles = tv({
	slots: {
		row: 'group border-b border-gray-100 transition-colors data-[dragging]:opacity-50 hover:bg-gray-50 selected:bg-primary-50/40',
		controlCell: 'w-8 py-3 pl-2 pr-1',
		dragButton: 'cursor-grab text-gray-400 active:cursor-grabbing hover:text-gray-700',
		expandIcon: 'transition-transform duration-150',
		cell: 'p-3',
		detailRow: 'bg-gray-50/60',
		detailSpacerCell: 'border-t border-gray-100',
		detailCell: 'border-t border-gray-100 px-4 py-3'
	},
	variants: {
		firstColumn: {
			true: { cell: 'pl-4 sm:pl-6' }
		},
		lastColumn: {
			true: { cell: 'pr-4 sm:pr-6' }
		},
		whitespaceNowrap: {
			true: { cell: 'whitespace-nowrap' }
		},
		expanded: {
			true: { expandIcon: 'rotate-90 text-green-700' }
		},
		hasLeading: {
			false: { detailCell: 'pl-4 sm:pl-6' }
		},
		align: {
			left: {
				cell: 'text-left'
			},
			center: {
				cell: 'text-center'
			},
			right: {
				cell: 'text-right'
			},
			actions: {
				cell: 'text-center'
			}
		}
	},
	defaultVariants: {
		firstColumn: false,
		lastColumn: false,
		whitespaceNowrap: false,
		expanded: false,
		hasLeading: true,
		align: 'left'
	}
});

type DataTableRowProps<T> = {
	rowId: string;
	row: T;
	index: number;
	columns: DataTableColumnProps<T>[];
	showSelectionCol: boolean;
	showDragCol: boolean;
	showExpandCol: boolean;
	hasLeading: boolean;
	rowClassName: string;
	detailContent: ReactNode;
	hasDetail: boolean;
	detailExpanded: boolean;
	onToggleDetail: () => void;
	onRowClick?: (row: T) => void;
};

export function DataTableRow<T>({
	rowId,
	row,
	index,
	columns,
	showSelectionCol,
	showDragCol,
	showExpandCol,
	hasLeading,
	rowClassName,
	detailContent,
	hasDetail,
	detailExpanded,
	onToggleDetail,
	onRowClick
}: DataTableRowProps<T>) {
	const styles = dataTableRowStyles();

	return (
		<>
			<Row id={rowId} className={styles.row({ className: rowClassName })}>
				{showSelectionCol && (
					<Cell className={styles.controlCell()}>
						<Checkbox slot="selection" label="Select Row" hideCheckboxLabel />
					</Cell>
				)}
				{showDragCol && (
					<Cell className={styles.controlCell()}>
						<AriaButton slot="drag" className={styles.dragButton()} aria-label="Drag to reorder">
							<GripVertical size={15} />
						</AriaButton>
					</Cell>
				)}
				{showExpandCol && (
					<Cell className={styles.controlCell()}>
						{hasDetail ? (
							<Button
								appearance="ghost"
								Icon={ChevronRight}
								iconClassName={styles.expandIcon({ expanded: detailExpanded })}
								aria-label="Expand"
								onClick={onToggleDetail}
							/>
						) : (
							<span className="inline-block w-6" />
						)}
					</Cell>
				)}
				{columns.map((x, colIndex) => (
					<Cell
						key={x.key}
						className={cn(
							styles.cell({
								firstColumn: colIndex === 0 && !hasLeading,
								lastColumn: colIndex === columns.length - 1,
								whitespaceNowrap: x.whitespaceNowrap,
								align: x.key === 'actions' ? 'actions' : x.cellAlign
							}),
							x.width,
							x.cellClassName
						)}
					>
						<RenderCell column={x} row={row} index={index} onRowClick={onRowClick} />
					</Cell>
				))}
			</Row>
			{detailExpanded && hasDetail && (
				<Row id={`${rowId}__detail`} className={styles.detailRow()}>
					{showSelectionCol && <Cell className={styles.detailSpacerCell()} />}
					{showDragCol && <Cell className={styles.detailSpacerCell()} />}
					{showExpandCol && <Cell className={styles.detailSpacerCell()} />}
					<Cell colSpan={columns.length} className={styles.detailCell({ hasLeading })}>
						<SuspenseWrapper name="DataTableRowDetail">{detailContent}</SuspenseWrapper>
					</Cell>
				</Row>
			)}
		</>
	);
}

function RenderCell<T>({
	column,
	row,
	index,
	onRowClick
}: {
	column: DataTableColumnProps<T>;
	row: T;
	index: number;
	onRowClick?: (row: T) => void;
}) {
	if (column.renderCell) {
		return column.renderCell(row, index, onRowClick);
	}
	const value = (row as Record<string, unknown>)[column.key];
	if (column.type === 'phone') {
		return formatPhoneNumber(value as string);
	}
	return value as ReactNode;
}
